<template>
  <Transition name="wallet-ban-notice">
    <aside
      v-if="isVisible"
      class="wallet-ban-notice"
      role="alert"
      aria-live="polite"
      :aria-label="t('walletTransferBan.title')"
    >
      <span class="wallet-ban-banner__icon" aria-hidden="true">
        <i class="fas fa-ban"></i>
      </span>
      <div class="wallet-ban-banner__body">
        <p class="wallet-ban-banner__title">{{ t('walletTransferBan.title') }}</p>
        <ul class="wallet-ban-banner__list">
          <li>{{ t('walletTransferBan.prohibition1') }}</li>
          <li>{{ t('walletTransferBan.prohibition2') }}</li>
          <li>{{ t('walletTransferBan.prohibition3') }}</li>
          <li>{{ t('walletTransferBan.prohibition4') }}</li>
        </ul>
      </div>
      <button
        type="button"
        class="wallet-ban-notice__close"
        :aria-label="t('a11y.closeNotification')"
        @click="close"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    </aside>
  </Transition>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from '@/composables/useI18n'
import { SESSION_EXPIRED_NOTICE_KEY } from '@/stores/user'

const AUTO_DISMISS_MS = 15_000

const { t } = useI18n()
const isVisible = ref(false)
let dismissTimer: ReturnType<typeof setTimeout> | undefined

onMounted(() => {
  try {
    isVisible.value = localStorage.getItem(SESSION_EXPIRED_NOTICE_KEY) === '1'
    if (isVisible.value) {
      localStorage.removeItem(SESSION_EXPIRED_NOTICE_KEY)
      dismissTimer = setTimeout(close, AUTO_DISMISS_MS)
    }
  } catch {
    isVisible.value = false
  }
})

onBeforeUnmount(() => {
  if (dismissTimer) clearTimeout(dismissTimer)
})

function close() {
  if (dismissTimer) {
    clearTimeout(dismissTimer)
    dismissTimer = undefined
  }
  isVisible.value = false
}
</script>

<style scoped>
.wallet-ban-notice {
  position: fixed;
  top: calc(var(--header-height, 64px) + 0.5rem);
  right: 1rem;
  z-index: 1200;
  width: min(23rem, calc(100vw - 1.5rem));
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem 2.75rem 1rem 1rem;
  background: var(--background-secondary);
  border: 1px solid rgba(var(--color-danger-rgb), 0.32);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.wallet-ban-banner__icon {
  flex-shrink: 0;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: var(--radius-circle);
  background: rgba(var(--color-danger-rgb), 0.16);
  color: var(--color-danger);
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wallet-ban-banner__title {
  margin: 0 0 0.4rem;
  color: var(--color-danger);
  font-weight: var(--font-weight-extrabold);
  font-size: var(--text-xs);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
}

.wallet-ban-banner__list {
  margin: 0;
  padding-left: 1.05rem;
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-snug);
}

.wallet-ban-banner__list li {
  margin-bottom: 0.35rem;
}

.wallet-ban-banner__list li:last-child {
  margin-bottom: 0;
}

.wallet-ban-notice__close {
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--radius-circle);
  background: transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.wallet-ban-notice__close:hover {
  background: rgba(var(--color-danger-rgb), 0.12);
  color: var(--text-primary);
}

.wallet-ban-notice__close:focus-visible {
  outline: 3px solid var(--color-danger);
  outline-offset: 2px;
}

.wallet-ban-notice-enter-active,
.wallet-ban-notice-leave-active {
  transition: opacity var(--transition-normal), transform var(--transition-normal);
}

.wallet-ban-notice-enter-from,
.wallet-ban-notice-leave-to {
  opacity: 0;
  transform: translateY(-0.75rem);
}

@media (prefers-reduced-motion: reduce) {
  .wallet-ban-notice-enter-active,
  .wallet-ban-notice-leave-active {
    transition: none;
  }
}

@media (max-width: 768px) {
  .wallet-ban-notice {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.875rem 2.6rem 0.875rem 0.875rem;
  }
}
</style>
